import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { HeroSection } from '@/components/HeroSection';
import { GPUComparisonTable } from '@/components/GPUComparisonTable';
import { BenchmarkCharts } from '@/components/BenchmarkCharts';
import { GPUQuiz } from '@/components/GPUQuiz';
import { PriceTracker } from '@/components/PriceTracker';
import { BuildCompatibilityChecker } from '@/components/BuildCompatibilityChecker';
import { MethodologySection } from '@/components/MethodologySection';
import { FAQSection } from '@/components/FAQSection';
import { Footer } from '@/components/Footer';
import { useScrollToHash } from '@/hooks/useScrollToHash';

const GPUComparison = () => {
  useScrollToHash();

  return (
    <>
      <Helmet>
        <title>GPU Comparison Chart 2024 - Compare All Graphics Cards | NVIDIA, AMD, Intel</title>
        <meta 
          name="description" 
          content="Complete GPU comparison chart ranking all graphics cards from NVIDIA, AMD, and Intel. Find the best graphics card for gaming with our performance rankings and price comparisons." 
        />
        <meta name="keywords" content="GPU comparison, graphics card comparison, best GPU, NVIDIA vs AMD, RTX 5090, RX 7900 XTX, gaming graphics card" />
        <link rel="canonical" href="https://gpucomparison.us/gpu-comparison" />
        
        {/* Open Graph */}
        <meta property="og:title" content="GPU Comparison Chart 2024 - Compare All Graphics Cards" />
        <meta property="og:description" content="Complete GPU comparison ranking all modern graphics cards by performance. Find the best GPU for your budget." />
        <meta property="og:type" content="website" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "GPU Comparison Chart",
            "description": "Complete graphics card comparison ranking all modern GPUs from NVIDIA, AMD, and Intel by performance.",
            "mainEntity": {
              "@type": "ItemList",
              "name": "Graphics Card Rankings",
              "description": "GPUs ranked by gaming performance relative to RTX 4090",
              "numberOfItems": 26
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-1">
          <HeroSection />

          <section id="quiz">
            <GPUQuiz />
          </section>
          
          <section id="comparison">
            <GPUComparisonTable />
          </section>

          <section id="benchmarks">
            <BenchmarkCharts />
          </section>

          <section id="prices">
            <PriceTracker />
          </section>

          <section id="compatibility">
            <BuildCompatibilityChecker />
          </section>
          
          <section id="methodology">
            <MethodologySection />
          </section>

          <section id="faq">
            <FAQSection />
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default GPUComparison;
